import { z } from 'zod';

import type { PortfolioPhoto } from '@/types/portfolio';

export const POST_TYPES = ['work', 'beforeAfter', 'service', 'openSlots', 'card', 'promo'] as const;
export type PostType = (typeof POST_TYPES)[number];

export const POST_STYLES = ['editorial', 'frame', 'block'] as const;
export type PostStyle = (typeof POST_STYLES)[number];

export const POST_FORMATS = ['story', 'portrait', 'square'] as const;
export type PostFormat = (typeof POST_FORMATS)[number];

/** Largura ÷ altura de cada formato de rede social. */
export const FORMAT_RATIO: Record<PostFormat, number> = {
  story: 9 / 16,
  portrait: 4 / 5,
  square: 1,
};

export const FORMAT_LABEL: Record<PostFormat, string> = {
  story: 'Story 9:16',
  portrait: 'Feed 4:5',
  square: 'Feed 1:1',
};

/** Tipos que dependem de uma foto do portfólio. */
export const PHOTO_TYPES: PostType[] = ['work', 'beforeAfter', 'service'];

/** Laranja do produto: o botão "Agende em" sobre a foto sai sempre nele. */
export const CTA_ORANGE = '#FD5001';

const DEFAULT_ACCENT = '#E8B04A';

/** Pares [cor principal, destaque]. O primeiro é sempre a cor da marca. */
const PRESET_PALETTES: [string, string][] = [
  ['#18181B', '#FD5001'],
  ['#1F3A5F', '#E8B04A'],
  ['#2F4A3A', '#E9D8A6'],
  ['#6B2E3E', '#F2C6B4'],
];

export function buildPalettes(brandColor: string): [string, string][] {
  return [[brandColor, DEFAULT_ACCENT], ...PRESET_PALETTES];
}

export interface PostService {
  id: string;
  name: string;
  /** Já formatado (`R$ 55`); nulo quando o preço não aparece no público. */
  price: string | null;
  durationMinutes: number | null;
}

export interface PostSlotDay {
  /** `Qua` */
  weekday: string;
  times: string[];
}

/** Tudo o que o post sabe sobre o negócio — vem do perfil, portfólio, serviços e agenda. */
export interface PostData {
  businessName: string;
  logoUrl: string | null;
  category: string | null;
  place: string | null;
  /** `controlapag.com.br/@navalha` */
  displayUrl: string;
  publicUrl: string;
  photos: PortfolioPhoto[];
  services: PostService[];
  slots: PostSlotDay[];
}

export const postSchema = z
  .object({
    type: z.enum(POST_TYPES, { message: 'Escolha o que divulgar.' }),
    photoId: z.string().nullable(),
    serviceId: z.string().nullable(),
    kicker: z.string().max(40, 'Chamada com até 40 letras.'),
    title: z.string().trim().min(1, 'Dê um título ao post.').max(60, 'Título com até 60 letras.'),
    subtitle: z.string().max(90, 'Linha de apoio com até 90 letras.'),
    highlight: z.string().max(10, 'Destaque com até 10 letras.'),
    showPrice: z.boolean(),
    showQr: z.boolean(),
    style: z.enum(POST_STYLES),
    format: z.enum(POST_FORMATS),
    palette: z.number().int().min(0),
  })
  .superRefine((v, ctx) => {
    if (PHOTO_TYPES.includes(v.type) && !v.photoId) {
      ctx.addIssue({ code: 'custom', path: ['photoId'], message: 'Escolha uma foto.' });
    }
    if (v.type === 'service' && !v.serviceId) {
      ctx.addIssue({ code: 'custom', path: ['serviceId'], message: 'Escolha o serviço.' });
    }
    if (v.type === 'promo' && !v.highlight.trim()) {
      ctx.addIssue({ code: 'custom', path: ['highlight'], message: 'Escreva o destaque.' });
    }
  });

export type PostForm = z.infer<typeof postSchema>;

/** Campos de cada etapa — o portão do assistente valida só estes. */
export const STEP_FIELDS: (keyof PostForm)[][] = [
  ['type'],
  ['photoId', 'serviceId', 'kicker', 'title', 'subtitle', 'highlight'],
  ['style', 'format', 'palette'],
  [],
];

export function formatPostPrice(value: number): string {
  return Number.isInteger(value)
    ? `R$ ${value}`
    : `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

export function formatPostDuration(minutes: number | null): string | null {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

function joinDot(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(' · ');
}

export function photoService(data: PostData, photo: PortfolioPhoto | undefined) {
  return photo?.serviceId ? data.services.find((s) => s.id === photo.serviceId) : undefined;
}

/** Foto que combina com o serviço; sem vínculo, a primeira do portfólio. */
export function photoForService(data: PostData, serviceId: string | null) {
  return data.photos.find((p) => p.serviceId === serviceId) ?? data.photos[0];
}

type PostText = Pick<PostForm, 'kicker' | 'title' | 'subtitle' | 'highlight'>;

/**
 * Texto de partida de cada tipo, montado com os dados reais. Promoção nasce em
 * branco: o desconto é decisão do prestador, não algo que a tela sugere.
 */
export function defaultText(
  type: PostType,
  data: PostData,
  photo: PortfolioPhoto | undefined,
  service: PostService | undefined,
): PostText {
  const linked = photoService(data, photo);
  switch (type) {
    case 'work':
      return {
        kicker: 'Trabalho de hoje',
        title: photo?.caption || linked?.name || 'Trabalho concluído',
        subtitle: joinDot(linked?.name, 'agende pelo link'),
        highlight: '',
      };
    case 'beforeAfter':
      return {
        kicker: 'Antes e depois',
        title: photo?.caption || 'Transformação completa',
        subtitle: joinDot(linked?.name, formatPostDuration(linked?.durationMinutes ?? null)),
        highlight: '',
      };
    case 'service':
      return {
        kicker: 'Serviço',
        title: service?.name ?? '',
        subtitle: joinDot(formatPostDuration(service?.durationMinutes ?? null), 'agende pelo link'),
        highlight: service?.price ?? '',
      };
    case 'openSlots':
      return {
        kicker: 'Agenda aberta',
        title: 'Horários livres essa semana',
        subtitle: 'Garanta o seu pelo link',
        highlight: '',
      };
    case 'card':
      return {
        kicker: joinDot(data.category, data.place),
        title: data.businessName,
        subtitle: 'Agende online em segundos',
        highlight: '',
      };
    case 'promo':
      return { kicker: '', title: '', subtitle: '', highlight: '' };
  }
}

// --- contraste -------------------------------------------------------------

function luminance(hex: string): number {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [n >> 16, (n >> 8) & 255, n & 255]
    .map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    })
    .reduce((acc, v, i) => acc + v * [0.2126, 0.7152, 0.0722][i], 0);
}

/**
 * Cor de destaque só vale se der pra ler sobre o fundo; senão cai para
 * branco ou quase-preto. Evita a chamada sumir numa paleta ruim.
 */
export function readable(fg: string, bg: string): string {
  const a = luminance(fg);
  const b = luminance(bg);
  const ratio = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  if (ratio >= 3) return fg;
  return b > 0.4 ? '#191919' : '#FFFFFF';
}

/** Mistura `hex` com preto (amount > 0) — fundo em degradê dos posts sem foto. */
export function darken(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  const ch = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v * (1 - amount)));
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

// --- legenda ---------------------------------------------------------------

function hashtag(text: string | null): string | null {
  if (!text) return null;
  const tag = text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
  return tag ? `#${tag}` : null;
}

/** Legenda pronta para colar no Instagram/WhatsApp. `variant` alterna a abertura. */
export function buildCaption(v: PostForm, data: PostData, variant: number): string {
  const alt = variant % 2 === 1;
  let body: string;
  switch (v.type) {
    case 'work':
      body = alt ? `Saiu hoje aqui: ${v.title} ✂️` : `${v.title} ✂️\n${v.subtitle}`;
      break;
    case 'beforeAfter':
      body = alt ? `Olha a diferença 👀\n${v.title}` : 'Antes e depois de hoje 🔥\nArrasta pra ver a diferença.';
      break;
    case 'service':
      body = alt
        ? `Bora cuidar do visual? ${v.title}${v.showPrice && v.highlight ? ` por ${v.highlight}` : ''}.`
        : `${v.title}${v.showPrice && v.highlight ? ` por ${v.highlight}` : ''}\n${v.subtitle}`;
      break;
    case 'openSlots':
      body = `${alt ? 'Ainda dá tempo 👇' : 'Ainda tem horário essa semana 👇'}\n${data.slots
        .map((d) => `${d.weekday}: ${d.times.join(', ')}`)
        .join('\n')}`;
      break;
    case 'card':
      body = `${alt ? 'Prazer, somos a' : 'Conheça a'} ${data.businessName}!\n${data.services
        .filter((s) => s.price)
        .slice(0, 4)
        .map((s) => `• ${s.name} — ${s.price}`)
        .join('\n')}`;
      break;
    case 'promo':
      body = alt
        ? `${v.highlight} ${v.title} 🔥\n${v.subtitle}`
        : `${v.title}! 🔥\n${v.subtitle}${v.kicker ? `\n${v.kicker}.` : ''}`;
      break;
  }
  const where = data.place ? `\n\n📍 ${data.place}` : '\n';
  const tags = [hashtag(data.category), hashtag(data.place)].filter(Boolean).join(' ');
  return `${body.trim()}${where}\n📅 Agende pelo link: ${data.displayUrl}${tags ? `\n\n${tags}` : ''}`;
}
